import { useContext, useState } from 'react';
import { useCallback } from 'react';
import { HimmaContext } from '../Context/HimmaContext';
import { CalculateFeaturedPriceRequest, CalculateFeaturedPriceResponse, PurchaseFeaturedStoreRequest, PurchaseFeaturedStoreResponse, VerifyFeaturedPaymentRequest, VerifyFeaturedPaymentResponse } from '../client-sdk';

export const useFeaturedRestaurantApi = () => {
  const api = useContext(HimmaContext)!;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const calculateFeaturedPrice = useCallback(
    async (request: CalculateFeaturedPriceRequest): Promise<CalculateFeaturedPriceResponse> => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.calculateFeaturedPrice(request);
        return response.data;
      } catch (err) {
        // Extract the backend error message if available
        let errorMessage = 'An error occurred while calculating price';
        
        if (err && typeof err === 'object' && 'response' in err) {
          const axiosError = err as { response?: { data?: { message?: string } }; message?: string };
          if (axiosError.response?.data?.message) {
            errorMessage = axiosError.response.data.message;
          } else if (axiosError.message) {
            errorMessage = axiosError.message;
          }
        } else if (err instanceof Error) {
          errorMessage = err.message;
        }
        
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const purchaseFeaturedStore = useCallback(
    async (request: PurchaseFeaturedStoreRequest): Promise<PurchaseFeaturedStoreResponse> => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.purchaseFeaturedStore(request);
        const data = response.data as any;
        data.customParameters = {subscriptionId : response.data.subscriptionId, merchantId: request.merchantId};
        return data;
      } catch (err) {
        // Extract the backend error message if available
        let errorMessage = 'An error occurred while purchasing featured store';
        
        if (err && typeof err === 'object' && 'response' in err) {
          const axiosError = err as { response?: { data?: { message?: string } }; message?: string };
          if (axiosError.response?.data?.message) {
            errorMessage = axiosError.response.data.message;
          } else if (axiosError.message) {
            errorMessage = axiosError.message;
          }
        } else if (err instanceof Error) {
          errorMessage = err.message;
        }
        
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const verifyFeaturedPayment = useCallback(
    async (request: VerifyFeaturedPaymentRequest): Promise<VerifyFeaturedPaymentResponse> => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.verifyFeaturedPayment(request);
        return response.data;
      } catch (err) {
        // Extract the backend error message if available
        let errorMessage = 'An error occurred while verifying payment';
        
        if (err && typeof err === 'object' && 'response' in err) {
          const axiosError = err as { response?: { data?: { message?: string } }; message?: string };
          if (axiosError.response?.data?.message) {
            errorMessage = axiosError.response.data.message;
          } else if (axiosError.message) {
            errorMessage = axiosError.message;
          }
        } else if (err instanceof Error) {
          errorMessage = err.message;
        }
        
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );


  return {
    calculateFeaturedPrice,
    purchaseFeaturedStore,
    verifyFeaturedPayment,
    loading,
    error,
  };
};
